import api from './api';
import type { InformeParametro } from './informesStoreService';
import { parsePdfError } from './informesStoreService';

export interface InformeBibliotecaListItem {
  id: number;
  codigo: string;
  modulo: string;
  titulo: string;
  descripcion?: string | null;
  planRequerido: string;
  activo: boolean;
  versionPublicadaNumero?: number | null;
  versionesCount: number;
  instalacionesCount: number;
  fechaAltaUtc: string;
  fechaModificacionUtc?: string | null;
}

export interface InformeBibliotecaVersion {
  id: number;
  numero: number;
  tipoCambio: string;
  notas?: string | null;
  nombreArchivoOriginal: string;
  estado: string;
  parametrosCount: number;
  parametros: InformeParametro[];
  fechaAltaUtc: string;
  fechaPublicacionUtc?: string | null;
  usuarioAlta?: string | null;
  usuarioPublicacion?: string | null;
  esPublicada: boolean;
}

export interface InformeBibliotecaDetail extends InformeBibliotecaListItem {
  versionPublicadaId?: number | null;
  versiones: InformeBibliotecaVersion[];
}

export interface InformeBibliotecaInstalacion {
  tenantId: number;
  tenantCode: string;
  tenantName: string;
  instalado: boolean;
  versionFijadaId?: number | null;
  versionFijadaNumero?: number | null;
  fechaPickUtc: string;
  usuarioPick?: string | null;
  fechaUnpickUtc?: string | null;
  usuarioUnpick?: string | null;
}

export interface InformeBibliotecaCreatePayload {
  codigo: string;
  titulo: string;
  descripcion?: string | null;
  modulo?: string;
  planRequerido?: string;
}

export interface InformeBibliotecaUpdatePayload {
  titulo: string;
  descripcion?: string | null;
  modulo?: string;
  planRequerido?: string;
  activo: boolean;
}

const BASE = '/api/admin/informes-biblioteca';

const informesBibliotecaAdminService = {
  list: async (): Promise<InformeBibliotecaListItem[]> => {
    const response = await api.get<InformeBibliotecaListItem[]>(BASE);
    return Array.isArray(response.data) ? response.data : [];
  },

  getById: async (id: number): Promise<InformeBibliotecaDetail> => {
    const response = await api.get<InformeBibliotecaDetail>(`${BASE}/${id}`);
    return response.data;
  },

  create: async (payload: InformeBibliotecaCreatePayload): Promise<InformeBibliotecaDetail> => {
    const response = await api.post<InformeBibliotecaDetail>(BASE, payload);
    return response.data;
  },

  update: async (id: number, payload: InformeBibliotecaUpdatePayload): Promise<InformeBibliotecaDetail> => {
    const response = await api.put<InformeBibliotecaDetail>(`${BASE}/${id}`, payload);
    return response.data;
  },

  uploadVersion: async (
    id: number,
    archivo: File,
    tipoCambio: string,
    notas?: string,
  ): Promise<InformeBibliotecaVersion> => {
    const formData = new FormData();
    formData.append('archivo', archivo);
    formData.append('tipoCambio', tipoCambio);
    if (notas) formData.append('notas', notas);
    const response = await api.post<InformeBibliotecaVersion>(`${BASE}/${id}/versiones`, formData, {
      transformRequest: [
        (body, headers) => {
          delete headers['Content-Type'];
          return body as FormData;
        },
      ],
    });
    return response.data;
  },

  publish: async (id: number, versionId: number, promocionar: boolean): Promise<InformeBibliotecaDetail> => {
    const response = await api.post<InformeBibliotecaDetail>(`${BASE}/${id}/versiones/${versionId}/publicar`, {
      promocionar,
    });
    return response.data;
  },

  promote: async (id: number, versionId: number): Promise<InformeBibliotecaDetail> => {
    const response = await api.post<InformeBibliotecaDetail>(`${BASE}/${id}/versiones/${versionId}/promocionar`);
    return response.data;
  },

  listInstalaciones: async (id: number): Promise<InformeBibliotecaInstalacion[]> => {
    const response = await api.get<InformeBibliotecaInstalacion[]>(`${BASE}/${id}/instalaciones`);
    return Array.isArray(response.data) ? response.data : [];
  },

  setPin: async (id: number, tenantId: number, versionId: number | null): Promise<InformeBibliotecaInstalacion> => {
    const response = await api.put<InformeBibliotecaInstalacion>(`${BASE}/${id}/instalaciones/${tenantId}/pin`, {
      versionId,
    });
    return response.data;
  },

  previewPdf: async (id: number, versionId: number, parametros?: Record<string, unknown>): Promise<Blob> => {
    try {
      const response = await api.post(
        `${BASE}/${id}/versiones/${versionId}/preview`,
        { parametros: parametros || {} },
        { responseType: 'blob' },
      );
      return response.data as Blob;
    } catch (err: unknown) {
      return parsePdfError(err);
    }
  },
};

export default informesBibliotecaAdminService;
